'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

// Dev only: shows a top bar and a Loading pill as soon as an internal link is clicked, until the route changes.
// Purely visual (pointer-events none); mounted only under NODE_ENV development in layout.tsx.
export default function DevNavLoader() {
  const pathname = usePathname();
  const [active, setActive] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const stop = () => {
    if (timer.current) clearTimeout(timer.current);
    setActive(false);
  };

  // Start the indicator when an internal link is clicked (Next <Link> renders an <a>).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      // internal navigations only - skip new-tab, downloads, external, hash-only, same-url
      if (!href.startsWith('/') || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      if (href === pathname + window.location.search) return;
      setActive(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setActive(false), 30000); // safety: never stick
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [pathname]);

  // Route actually changed -> the new page is rendering, clear the indicator.
  useEffect(() => {
    stop();
    return stop;
  }, [pathname]);

  if (!active) return null;

  return (
    <div aria-hidden='true' style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 3, overflow: 'hidden', background: 'rgba(56,189,248,.15)' }}>
        <div style={{ height: '100%', width: '40%', background: '#38bdf8', animation: 'devnav-slide 1.1s ease-in-out infinite' }} />
      </div>
      <div
        style={{
          position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(15,23,42,.92)', color: '#e2e8f0', fontSize: 12,
          padding: '6px 12px', borderRadius: 9999, boxShadow: '0 2px 10px rgba(0,0,0,.25)',
        }}
      >
        <span
          style={{
            width: 12, height: 12, borderRadius: '50%', display: 'inline-block',
            border: '2px solid #38bdf8', borderTopColor: 'transparent', animation: 'devnav-spin .7s linear infinite',
          }}
        />
        Loading...
      </div>
      <style>{`@keyframes devnav-spin{to{transform:rotate(360deg)}}@keyframes devnav-slide{0%{transform:translateX(-120%)}100%{transform:translateX(360%)}}`}</style>
    </div>
  );
}
